import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Shield,
  Lock,
  Clock,
  Heart,
  FlaskConical,
  Stethoscope,
  CalendarCheck,
  CheckCircle,
  ArrowRight,
  MessageCircle,
  ClipboardCheck,
  Home as HomeIcon,
  EyeOff,
  Sparkles,
  Flower2,
  Sun,
  Leaf,
  Moon,
  Zap,
} from 'lucide-react';
import { markCTAClicked, markAssessmentStarted } from '../components/EmailCapturePopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';

const Home = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const handleCTAClick = () => {
    markCTAClicked();
    markAssessmentStarted();
  };

  const scrollToBooking = () => {
    handleCTAClick();
    window.location.href = '/book';
  };

  const urgencyText = "Consultations are free but limited, we have 4 spots open this week.";

  // --- Section data ---
  const painCards = [
    {
      quote: 'My marriage is falling apart and I do not even know why.',
      explain: 'Plummeting hormones tank libido, fuel resentment, and quietly erode the closeness you used to take for granted. This is rarely a "you" problem.'
    },
    {
      quote: 'I look in the mirror and do not recognize the woman staring back.',
      explain: 'Weight that will not move, hair you do not recognize, skin that is not yours, your body did not change because you stopped trying. Your hormones changed.'
    },
    {
      quote: 'I am exhausted in a way coffee, sleep, and supplements cannot touch.',
      explain: 'Bone-deep fatigue is almost always hormonal, thyroid, adrenal, or sex hormone driven. It is not laziness. It is not age. It is treatable.'
    },
    {
      quote: 'I am snapping at people I love and I cannot stop.',
      explain: 'Mood swings, rage, and tears that arrive out of nowhere are biology, not bad behavior. The right treatment quiets the storm without numbing you out.'
    },
    {
      quote: 'My body is doing things I do not understand.',
      explain: 'Hot flashes, night sweats, irregular cycles, weight redistribution, joint pain, these are not random. They have names, causes, and protocols that actually work.'
    },
    {
      quote: 'I do not even know who I am anymore.',
      explain: 'Identity loss is the loneliest part of hormonal change, and the part no one warned you about. The woman you are mourning is not gone. She is waiting for the support no one is giving you.'
    }
  ];

  const conditionTiles = [
    { Icon: Sun, name: 'Perimenopause', href: '/perimenopause', accent: 'gold' },
    { Icon: Leaf, name: 'Menopause', href: '/menopause', accent: 'rose' },
    { Icon: Flower2, name: 'PMOS', href: '/pmos', accent: 'rose' },
    { Icon: Moon, name: 'PMDD', href: '/pmdd', accent: 'mauve' },
    { Icon: Heart, name: 'Postpartum', href: '/postpartum', accent: 'blush' },
    { Icon: Zap, name: 'Thyroid', href: '/thyroid', accent: 'sage' },
  ];

  const telehealthBenefits = [
    {
      Icon: Lock,
      title: 'Privacy, finally',
      body: 'No waiting room. No "who is she here for." Your symptoms are personal, your appointments should feel that way too.'
    },
    {
      Icon: HomeIcon,
      title: 'From your own couch',
      body: 'Consults between school pickup and dinner. No childcare to arrange. No half-day off work for a 7-minute visit.'
    },
    {
      Icon: Stethoscope,
      title: 'Direct to a hormone specialist',
      body: 'Skip the PCP referral runaround. You come straight to the clinicians who actually understand what your body is doing.'
    },
    {
      Icon: FlaskConical,
      title: 'Real testing, real answers',
      body: 'Comprehensive hormone, thyroid, and metabolic panels, done at a lab near you, results back in days, not months.'
    }
  ];

  const whyGlow = [
    {
      Icon: Shield,
      title: 'The 60-day promise',
      body: 'If you do not feel meaningfully better within 60 days of starting your plan, we keep working with you for free until you do. No questions asked.'
    },
    {
      Icon: ClipboardCheck,
      title: 'Transparent pricing',
      body: 'A simple monthly membership, labs, visits, messaging, and ongoing care included. No surprise bills. No upsells. Many patients use HSA/FSA dollars.'
    },
    {
      Icon: Sparkles,
      title: 'Hormone specialists, not generalists',
      body: 'We focus exclusively on women\'s hormones. That means we see the patterns most providers miss, and treat the cause, not the symptom.'
    },
    {
      Icon: FlaskConical,
      title: 'Full panels, not just TSH',
      body: 'Most "normal" labs miss most thyroid, hormonal, and metabolic problems. We run the labs that actually find what is happening, every time.'
    },
    {
      Icon: Clock,
      title: 'Real time with your clinician',
      body: 'No 7-minute appointments. No being talked over. You get a clinician who listens, asks the next question, and adjusts your plan with you.'
    },
    {
      Icon: MessageCircle,
      title: 'Care that does not stop after the visit',
      body: 'Direct messaging between visits, regular lab rechecks, and a care team that knows your name and your story. You are never on your own with this.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Book your free consult',
      description: 'A 15-minute call to hear what you are experiencing and confirm we are the right fit. No pressure. No commitment.',
    },
    {
      number: '2',
      title: 'Get the full panel',
      description: 'Comprehensive hormone, thyroid, and metabolic labs at a location near you, results back in days.',
    },
    {
      number: '3',
      title: 'Receive your personalized plan',
      description: 'A treatment plan built around your specific labs, symptoms, history, and goals. Not a one-size-fits-all script.',
    },
    {
      number: '4',
      title: 'Feel like yourself again',
      description: 'Most patients begin noticing changes in 2-4 weeks. Ongoing check-ins and adjustments keep you moving forward.',
    },
  ];

  const testimonials = [
    {
      quote: 'I lost 12 lbs and finally sleep through the night. For the first time in years, I wake up feeling rested.',
      name: 'Sarah M.',
      age: 47,
      photo: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=80&h=80&fit=crop&crop=face',
    },
    {
      quote: 'I thought feeling exhausted was just part of getting older. Within 6 weeks, my energy completely transformed.',
      name: 'Jennifer L.',
      age: 52,
      photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
    },
    {
      quote: 'My brain fog lifted and I stopped snapping at my family. I feel like myself again, the real me.',
      name: 'Michelle R.',
      age: 44,
      photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
    },
    {
      quote: 'After being dismissed by three doctors, someone finally listened. This changed my life.',
      name: 'Karen T.',
      age: 49,
      photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=face',
    },
  ];

  const faqs = [
    {
      question: 'Is this covered by insurance?',
      answer: 'Most of our patients pay a simple monthly membership, and many use HSA/FSA tax-free dollars to cover it. We walk you through exactly what your investment looks like on the free consult. No surprise bills, ever.',
    },
    {
      question: 'How fast will I see results?',
      answer: 'Most patients begin noticing improvements in energy, sleep, and mood within 2-4 weeks. Full hormone optimization typically takes 2-3 months as we fine-tune your plan with follow-up labs. Everyone is different, we monitor your progress closely.',
    },
    {
      question: 'What does treatment involve?',
      answer: 'Treatment is personalized to your labs, symptoms, and goals. It may include bioidentical hormone therapy, thyroid optimization, targeted supplements, GLP-1 medications when clinically appropriate, and lifestyle guidance. All visits are secure video, no office visits required.',
    },
    {
      question: 'Is hormone therapy safe?',
      answer: 'When properly prescribed and monitored, modern bioidentical hormone therapy has an excellent safety profile. We use evidence-based protocols, regular lab monitoring, and only FDA-approved medications. Your safety drives every decision we make.',
    },
    {
      question: 'How is this different from seeing my regular doctor?',
      answer: 'Most primary care visits last 7 minutes and run a single TSH for thyroid. We focus exclusively on women\'s hormone health, run full panels, take real time with you, and stay with you between visits via direct messaging. Different focus, different model, very different result.',
    },
    {
      question: 'Do I need labs first?',
      answer: 'Not before your free consult. As part of treatment we will order comprehensive labs, you can get them done at a location near you. If you have recent labs (within 3 months), bring them to your consult and we will review them together.',
    },
    {
      question: 'What states do you serve?',
      answer: 'We currently provide telehealth services throughout Utah, with more states coming soon. All consultations happen via secure video, and medications ship directly to your home.',
    },
  ];

  return (
    <div className="home-v2">
      {/* ============ HERO ============ */}
      <section className="home-v2-hero">
        <div className="container home-v2-hero-inner">
          <div className="home-v2-hero-content">
            <span className="home-v2-eyebrow">Telehealth Hormone Care for Women</span>
            <h1 className="home-v2-hero-headline">
              You used to feel like yourself.<br />
              <span className="home-v2-hero-accent">
                Then somewhere between being a wife, raising kids, and showing up for everyone else, you quietly got lost.
              </span>
            </h1>
            <p className="home-v2-hero-sub">
              For thousands of women between 35 and 55, this is not "just life." It is a hormonal shift no one warned you about, no one is treating you for, and almost no one is taking seriously. We do this differently.
            </p>
            <div className="home-v2-hero-actions">
              <button
                className="home-v2-btn home-v2-btn-primary home-v2-btn-large"
                onClick={scrollToBooking}
                data-testid="home-hero-cta"
              >
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <a href="#pain" className="home-v2-btn home-v2-btn-secondary" data-testid="home-hero-secondary-cta">
                See If This Sounds Like You
              </a>
            </div>
            <p className="home-v2-hero-note">{urgencyText}</p>
          </div>
          <div className="home-v2-hero-image">
            <img
              src="/hero-home.png?v=1"
              alt="A woman and her mother sharing a calm, hopeful moment"
            />
            <div className="home-v2-hero-badge">
              <Shield size={18} />
              <span>60-Day Promise</span>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* ============ PAIN GRID ============ */}
      <section className="home-v2-pain" id="pain">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">Does any of this sound like you?</span>
            <h2 className="home-v2-section-headline">The things you have only ever said to yourself</h2>
            <p className="home-v2-section-sub">
              Read these slowly. If even one feels like it was written about you, you are not crazy, you are not too much, and you are absolutely not alone.
            </p>
          </div>
          <div className="home-v2-pain-grid">
            {painCards.map((card, i) => (
              <div key={i} className="home-v2-pain-card" data-testid={`home-pain-card-${i}`}>
                <p className="home-v2-pain-quote">"{card.quote}"</p>
                <p className="home-v2-pain-explain">{card.explain}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VALIDATING BRIDGE ============ */}
      <section className="home-v2-bridge">
        <div className="container">
          <div className="home-v2-bridge-inner">            <span className="home-v2-eyebrow home-v2-eyebrow-light">The truth no one told you</span>
            <h2 className="home-v2-bridge-headline">
              You are not broken.<br />
              <em>Your hormones changed.</em>
            </h2>
            <p className="home-v2-bridge-body">
              Estrogen, progesterone, testosterone, thyroid, cortisol, these run your sleep, your weight, your mood, your sex drive, your sharpness, your sense of self. When even one shifts, everything you used to take for granted starts to slip. And when several shift at once, it can feel like the woman you were is being replaced by someone you do not recognize.
            </p>
            <p className="home-v2-bridge-body">
              That is not a personality flaw. It is not aging "gracefully." It is not something to push through with another supplement or another spin class. It is a hormonal reality, it is treatable, and you deserve a clinician who actually understands it.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CONDITIONS WE TREAT ============ */}
      <section className="home-v2-conditions">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">What we treat</span>
            <h2 className="home-v2-section-headline">Care built around what is actually happening</h2>
            <p className="home-v2-section-sub">
              Six focused programs designed for the hormonal moments women are most often dismissed in. Click any condition to see if your symptoms fit.
            </p>
          </div>
          <div className="home-v2-conditions-grid">
            {conditionTiles.map((c) => (
              <Link
                key={c.name}
                to={c.href}
                className={`home-v2-condition accent-${c.accent}`}
                data-testid={`home-condition-${c.name.toLowerCase()}`}
              >
                <div className="home-v2-condition-icon">
                  <c.Icon size={24} strokeWidth={1.6} />
                </div>
                <span className="home-v2-condition-name">{c.name}</span>
                <ArrowRight size={16} className="home-v2-condition-arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY TELEHEALTH ============ */}
      <section className="home-v2-telehealth">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">Why telehealth?</span>
            <h2 className="home-v2-section-headline">Because real care should fit your life, not the other way around</h2>
            <p className="home-v2-section-sub">
              You should not have to take a half-day off work, sit in a waiting room, and explain your story to a brand-new face every time, just to be told your labs "look fine."
            </p>
          </div>
          <div className="home-v2-telehealth-grid">
            {telehealthBenefits.map((b, i) => (
              <div key={i} className="home-v2-telehealth-card" data-testid={`home-telehealth-card-${i}`}>
                <div className="home-v2-telehealth-icon">
                  <b.Icon size={22} strokeWidth={1.6} />
                </div>
                <h3>{b.title}</h3>
                <p>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY GLOW HEALTH ============ */}
      <section className="home-v2-why">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">Why Glow Health</span>
            <h2 className="home-v2-section-headline">A different kind of clinic for a different kind of care</h2>
            <p className="home-v2-section-sub">
              Most providers were not trained for this. We were built for it.
            </p>
          </div>
          <div className="home-v2-why-grid">
            {whyGlow.map((w, i) => (
              <div key={i} className="home-v2-why-card" data-testid={`home-why-card-${i}`}>
                <div className="home-v2-why-icon">
                  <w.Icon size={20} strokeWidth={1.7} />
                </div>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="home-v2-how">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">How it works</span>
            <h2 className="home-v2-section-headline">From "something is wrong" to "I feel like myself again"</h2>
          </div>
          <div className="home-v2-how-grid">
            {steps.map((s) => (
              <div key={s.number} className="home-v2-how-card">
                <span className="home-v2-how-number">{s.number}</span>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
          <div className="home-v2-how-cta">
            <button
              className="home-v2-btn home-v2-btn-primary home-v2-btn-large"
              onClick={scrollToBooking}
              data-testid="home-how-cta"
            >
              Book Your Free Consultation
              <ArrowRight size={18} />
            </button>
            <p className="home-v2-cta-note">{urgencyText}</p>
          </div>
        </div>
      </section>

      {/* ============ MEET THE TEAM (vague) ============ */}
      <section className="home-v2-team">
        <div className="container">
          <div className="home-v2-team-inner">
            <div className="home-v2-team-content">
              <span className="home-v2-eyebrow">Your care team</span>
              <h2 className="home-v2-section-headline">Specialists who actually understand women's hormones</h2>
              <p className="home-v2-team-body">
                Our clinicians are board-certified, women's-hormone-focused providers who do not split their attention between fifteen specialties. They train in the nuances of perimenopause, PMOS (formerly PCOS), PMDD, postpartum, thyroid, and menopause, so when you walk in with a complicated story, you finally get someone who recognizes it.
              </p>
              <ul className="home-v2-team-list">
                <li><CheckCircle size={18} /> Board-certified, licensed providers</li>
                <li><CheckCircle size={18} /> Specialized in women's hormone medicine</li>
                <li><CheckCircle size={18} /> Trained in bioidentical and conventional protocols</li>
                <li><CheckCircle size={18} /> Committed to listening, not labeling</li>
              </ul>
            </div>
            <div className="home-v2-team-visual">
              <div className="home-v2-team-quote">
                <p>"You should not have to be in crisis to be taken seriously. You should not have to fight to be heard. Our entire model is built around making sure you never have to."</p>
                <span>, Glow Health care philosophy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="home-v2-testimonials">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">Real women, real results</span>
            <h2 className="home-v2-section-headline">When the right clinician finally looks</h2>
          </div>
          <div className="home-v2-testimonials-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="home-v2-testimonial">
                <p className="home-v2-testimonial-quote">"{t.quote}"</p>
                <div className="home-v2-testimonial-author">
                  <img src={t.photo} alt={t.name} />
                  <span>{t.name}, {t.age}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 60-DAY GUARANTEE ============ */}
      <section className="home-v2-promise">
        <div className="container">
          <div className="home-v2-promise-inner">
            <div className="home-v2-promise-badge">
              <Shield size={28} />
            </div>
            <span className="home-v2-eyebrow home-v2-eyebrow-light">Our 60-day promise</span>
            <h2 className="home-v2-promise-headline">
              If you do not feel meaningfully better in 60 days, we keep working with you, for free, until you do.
            </h2>
            <p className="home-v2-promise-body">
              No fine print. No expiration. No "well, technically." If your plan is not working, that is on us, and we stay on it until it does.
            </p>
            <button
              className="home-v2-btn home-v2-btn-light home-v2-btn-large"
              onClick={scrollToBooking}
              data-testid="home-promise-cta"
            >
              Book Your Free Consultation
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="home-v2-faq">
        <div className="container">
          <div className="home-v2-section-head">
            <span className="home-v2-eyebrow">Common questions</span>
            <h2 className="home-v2-section-headline">What women ask before they book</h2>
          </div>
          <div className="home-v2-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`home-v2-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button
                  className="home-v2-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`home-faq-toggle-${i}`}
                >
                  <span>{faq.question}</span>
                  {openFaq === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFaq === i && (
                  <div className="home-v2-faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="home-v2-final">
        <div className="container">
          <div className="home-v2-final-inner">
            <h2 className="home-v2-final-headline">You have lost enough of yourself.</h2>
            <p className="home-v2-final-body">
              Every month you wait is another month of pretending. Another season of dimming. Another year of being told you are fine when you know you are not. Fifteen minutes is all it takes to find out if real care is finally within reach.
            </p>
            <button
              className="home-v2-btn home-v2-btn-primary home-v2-btn-large"
              onClick={scrollToBooking}
              data-testid="home-final-cta"
            >
              Book Your Free Consultation
              <ArrowRight size={18} />
            </button>
            <p className="home-v2-final-note">15 minutes. No pressure. The version of you that you miss is closer than you think.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
